import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CONTACT_US_URL } from '@rci/self-serve-fido/app.constant';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { Button } from '@rogers/cdk/button';
import { Link } from '@rogers/cdk/link';
import { UserAccount } from '@rogers/self-serve-common';
import {
  AccountDetail,
  fetchAccountDetailSuccess,
  fetchAccountListSuccess,
  updateReasonAndLostOrStolen,
} from '@rogers/self-serve-common/account';
import * as targetedOffersLib from '@rogers/self-serve-targeted-offers';
import { tap, withLatestFrom } from 'rxjs/operators';
import { AlertFacadeService, AppAlert, AppAlertAnalyticsConfig, AppAlertType } from '@rogers/self-serve-common/alert';
import { Alert } from '@rogers/cdk/assembled/alert';

const infoCircleIcon = 'info-outlined';
const alertIcon = 'warning-outlined';

@Injectable()
export class AlertEffects {
  loadFraudAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchAccountListSuccess),
        tap(({ accounts }) => {
          this.addFraudAlerts(accounts, this.userInformationStateFacade.isSubscriber);
        }),
      ),
    { dispatch: false },
  );

  loadLostOrStolenAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchAccountDetailSuccess),
        tap(({ accountDetail }) => {
          this.addLostOrStolenAlerts(accountDetail);
        }),
      ),
    { dispatch: false },
  );

  loadLostOrStolenAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateReasonAndLostOrStolen),
        withLatestFrom(this.accountDetailService.getSelectedAccountDetail$()),
        tap(([payload, accountDetail]) => {
          const subscription = accountDetail.subscriptions.find(x => x.alias === payload.subscriptionAlias);
          if (subscription) {
            if (payload.isLostOrStolen) {
              this.addLostOrStolenAlerts(accountDetail);
            } else {
              this.alertFacadeService.dismissAlert(`${subscription.subNumber}_lost_or_stolen`);
            }
          }
        }),
      ),
    { dispatch: false },
  );

  targetedOfferAlert$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType<targetedOffersLib.GetTargetedOffersSuccess>(
          targetedOffersLib.TargetedOffersActionTypes.GetTargetedOffersSuccess,
        ),
        tap(({ payload }) => {
          this.addTargetedOfferAlerts(payload.offers);
        }),
      );
    },
    { dispatch: false },
  );

  constructor(
    private readonly accountDetailService: AccountDetailService,
    private readonly actions$: Actions,
    private readonly alertFacadeService: AlertFacadeService,
    private readonly userInformationStateFacade: UserInformationStateFacade,
  ) {}

  private addFraudAlerts(accounts: UserAccount[], isSubscriber: boolean) {
    if (!accounts || !accounts.some(account => account.fraud)) {
      return;
    }
    const contactUsCta: Button = {
      copy: 'maintenance.contact-us',
      val: CONTACT_US_URL,
      alt: 'maintenance.contact-us',
    };
    const alertMessageKey = isSubscriber
      ? 'overview.subscriber-fraud-account-alert-message'
      : 'overview.fraud-account-alert-message';
    accounts
      .filter(account => account.fraud)
      .forEach(account =>
        this.alertFacadeService.upsertAlert(
          this.getAlertObject(
            account.accountNumber,
            'fraud_ban',
            'profile',
            'error',
            alertMessageKey,
            alertIcon,
            'overview.fraud-account-alert-icon-alt',
            '',
            [contactUsCta],
            false,
          ),
        ),
      );
  }

  private addLostOrStolenAlerts(accountDetail: AccountDetail) {
    if (!accountDetail.subscriptions) {
      return;
    }
    accountDetail.subscriptions.forEach(subscription => {
      if (!subscription.isLostOrStolen) {
        return;
      }
      const lostOrStolenCta: Button = {
        copy: 'overview.reactivate-suspended-service-cta',
        alt: 'overview.reactivate-suspended-service-cta-alt',
      };
      this.alertFacadeService.upsertAlert(
        this.getAlertObject(
          subscription.subNumber,
          'lost_or_stolen',
          'subscriber',
          'warning',
          'overview.lost-or-stolen',
          alertIcon,
          'overview.lost-or-stolen-alert-icon-alt',
          'overview.lost-or-stolen-alert-close-alt',
          [lostOrStolenCta],
          true,
          '',
          2,
        ),
      );
    });
  }

  private addTargetedOfferAlerts(targetedOffers: targetedOffersLib.TargetedOffer[]) {
    targetedOffers.forEach(targetedOffer => {
      if (targetedOffer.tvmOffer || targetedOffer.standAloneBPO) {
        const targetedOfferAlertCta: Link = {
          copy: 'overview.targeted-offer-alert-cta',
          val: '',
          alt: 'overview.targeted-offer-alert-cta-alt',
        };
        this.alertFacadeService.upsertAlert(
          this.getAlertObject(
            targetedOffer.ctn,
            'targeted_offer',
            'subscriber',
            'info',
            'overview.targeted-offer-alert-body',
            infoCircleIcon,
            'overview.targeted-offer-alert-icon-alt',
            'overview.targeted-offer-alert-close-alt',
            [targetedOfferAlertCta],
            true,
            'overview.targeted-offer-alert-header',
            1,
          ),
        );
      }
    });
  }

  /* eslint-disable */
  private getAlertObject(
    key: string,
    uniqueAlertId: string,
    type: string,
    alertVariant: string,
    alertBody: string,
    alertIcon: string,
    alertIconCaption: string,
    alertCloseLabel: string,
    alertCta,
    showCloseButton: boolean,
    title = '',
    order = 999,
    analyticsConfig?: AppAlertAnalyticsConfig,
  ): AppAlert {
    return {
      key,
      uniqueAlertId,
      type: type as AppAlertType,
      order,
      alertData: {
        showCloseButton,
        variant: alertVariant as Alert['variant'],
        copy: {
          iconCaption: alertIconCaption,
          body: alertBody,
          closeLabel: alertCloseLabel,
          title,
        },
        cta: alertCta,
      },
      analyticsConfig,
      iconName: alertIcon,
    };
  }
}
